// Manual sections 1–5: what the tool is, who sees what, signing in, the interface,
// and the vocabulary the rest of the manual assumes. See ../index.js for the schema.

const ALL_ROLES = ['sAdm', 'adm', 'qa', 'callAgent'];

const gettingStarted = [
  {
    id: 'what-it-does',
    group: 'Start here',
    title: 'What the tool does',
    lede: 'Recording in, graded assessment out, with a person checking the grade.',
    roles: ALL_ROLES,
    blocks: [
      {
        type: 'p',
        text:
          'The tool takes contact centre call recordings, turns them into transcripts, grades each call against a **scorecard** using AI, and then puts a QA reviewer in front of that grade to confirm or correct it. Everything else in the console serves that path: getting recordings in, deciding what "good" means, handing calls to reviewers, and keeping a record of every change.',
      },
      {
        type: 'p',
        text:
          'A call moves in one direction: brought in, transcribed, scored by AI, reviewed, then marked assessed, which locks it. One thing can send it back: the call agent, or their General Manager, can **dispute** the assessment, and a reviewer then looks at it again.',
      },
      {
        type: 'note',
        variant: 'tip',
        tag: 'Where to start',
        text:
          'If you review calls, skip to {{Reviewing a call|#call-details}}. If you are setting the system up, read {{Campaigns|#campaigns}} and {{Scorecards|#scorecards}} first; everything else depends on them.',
      },
    ],
  },

  {
    id: 'roles',
    group: 'Start here',
    title: 'Who sees what',
    lede: 'Four roles. Your role decides your sidebar, your landing page, and what you can change.',
    roles: ALL_ROLES,
    blocks: [
      {
        type: 'p',
        text:
          'Your sign-in account has a role, and the console builds itself around it. If a page in this manual is not in your sidebar, your role does not have it. That is expected, not a fault.',
      },
      {
        type: 'defs',
        items: [
          {
            term: 'Super Admin',
            text: 'Everything an Admin can do, plus Reports, User Management (sign-in accounts), Agent Management (agent records and teams), the daily transcription limit, editing a call’s details, and unmarking a call that was already assessed.',
          },
          {
            term: 'Admin',
            text: 'Sets up campaigns and scorecards, brings recordings in, hands jobs to QA agents, resolves disputes, and reads the audit trail. Lands on **Campaign Home**.',
          },
          {
            term: 'QA Agent',
            text: 'The reviewer. Works the calls assigned to them, corrects and confirms the AI’s grades, resolves disputes, and can upload recordings. Lands on **Assigned to me**.',
          },
          {
            term: 'Call Agent',
            text: 'The person on the call. Sees their own calls under **My Calls**, reads the feedback, and can dispute an assessment. A call agent who is a **General Manager** also sees their team’s calls there (see {{the building blocks|#concepts}}).',
          },
        ],
      },
      {
        type: 'table',
        caption: 'Sidebar by role',
        head: ['Page', 'Super Admin', 'Admin', 'QA Agent', 'Call Agent'],
        rows: [
          ['Campaign Home', 'Yes', 'Yes', '—', '—'],
          ['Dashboard', 'Yes', 'Yes', '—', '—'],
          ['Reports', 'Yes', '—', '—', '—'],
          ['Search', 'Yes', 'Yes', '—', '—'],
          ['Scorecards', 'Yes', 'Yes', '—', '—'],
          ['Campaigns', 'Yes', 'Yes', '—', '—'],
          ['Jobs', 'Yes', 'Yes', '—', '—'],
          ['Scheduled Jobs', 'Yes', 'Yes', '—', '—'],
          ['Upload Recordings', 'Yes', 'Yes', 'Yes', '—'],
          ['Cloud Connector', 'Yes', 'Yes', '—', '—'],
          ['Assigned to me', '—', '—', 'Yes', '—'],
          ['My Calls', '—', '—', '—', 'Yes'],
          ['Disputes', 'Yes', 'Yes', 'Yes', '—'],
          ['Audit', 'Yes', 'Yes', '—', '—'],
          ['User Management', 'Yes', '—', '—', '—'],
          ['Agent Management', 'Yes', '—', '—', 'General Managers *'],
          ['Transcribe Quota', 'Yes', '—', '—', '—'],
          ['User Manual', 'Yes', 'Yes', 'Yes', 'Yes'],
        ],
        footnote:
          '* Not in the sidebar. A General Manager opens it with `Manage my team` on My Calls, and can only add agents to, or remove them from, their own team.',
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'More than one role',
        text:
          'If your account holds more than one role, the sidebar shows the menu of the highest one: Super Admin, then Admin, then QA Agent, then Call Agent.',
      },
    ],
  },

  {
    id: 'signing-in',
    group: 'Start here',
    title: 'Signing in',
    lede: 'First sign-in, password resets, verification codes, and how long you stay signed in.',
    roles: ALL_ROLES,
    blocks: [
      { type: 'h3', text: 'Your first sign-in' },
      {
        type: 'steps',
        items: [
          {
            title: 'Enter your username and password, then choose `Sign in`.',
            text: 'Your administrator creates the account and sends you a temporary password.',
          },
          {
            title: 'Choose your own password.',
            text:
              'Because the account was created with a temporary password, the next screen is **Choose a new password**. Enter it in `New password` and `Confirm new password`, then choose `Set password and continue`. The eye icon shows what you typed.',
          },
          {
            title: 'Enter your verification code, if you are asked for one.',
            text: 'Where multi-factor authentication is switched on, a **Verification required** screen follows. Type the code and choose `Verify`.',
          },
          {
            title: 'Take the welcome tour.',
            text:
              'The first time you sign in, a short tour runs. It takes about a minute and you can leave at any point with `Skip tour`. You can replay it later from the help panel.',
          },
        ],
      },
      {
        type: 'p',
        text:
          'On some installations the sign-in screen shows only a `Sign in` button, which takes you to a separate sign-in page. Enter the same details there.',
      },
      { type: 'h3', text: 'If you forget your password' },
      {
        type: 'p',
        text:
          'Choose `Forgot your password?` on the sign-in screen, enter your username and choose `Send reset code`. A code goes to the contact details on your account. Enter it with your new password twice and choose `Set new password`, then sign in.',
      },
      {
        type: 'note',
        variant: 'tip',
        tag: 'If you are told your password needs resetting',
        text:
          'If an administrator has reset your password, every password you try is rejected. The sign-in screen then sends you a reset code automatically and says so. Complete the reset and sign in with the new password. You do not need to contact anyone first.',
      },
      { type: 'h3', text: 'Staying signed in and signing out' },
      {
        type: 'ul',
        items: [
          'You stay signed in on this browser, even after closing it. The console renews your session in the background, so you are only asked to sign in again when the sign-in period your organisation set runs out.',
          'If you open a page while signed out, you return to that page after signing in.',
          'Sign out with `Sign Out` in your account menu or `Logout` at the bottom of the sidebar. Signing out in one tab signs out every tab, and the next sign-in asks for your password again, so always sign out on a shared computer.',
          'If an administrator changes your role or disables your account, the change reaches an open session when it next renews, within about an hour. Signing out and in again picks up a new role straight away.',
        ],
      },
    ],
  },

  {
    id: 'getting-around',
    group: 'Start here',
    title: 'Getting around',
    lede: 'The sidebar, the search box, the help button, and your account menu.',
    roles: ALL_ROLES,
    blocks: [
      { type: 'h3', text: 'The sidebar' },
      { type: 'p', text: 'For Admins and Super Admins the sidebar groups pages by what you are doing:' },
      {
        type: 'ul',
        items: [
          '**Overview**: Campaign Home and the Dashboard, plus Reports for Super Admins. Where things stand.',
          '**Quality**: Search, Scorecards and Campaigns. What "good" means and how to find calls.',
          '**Operations**: Jobs, Scheduled Jobs, Upload Recordings and Cloud Connector. Getting recordings in.',
          '**Administration**: Disputes and Audit, plus User Management, Agent Management and Transcribe Quota for Super Admins.',
          '**Help**: this User Manual.',
        ],
      },
      {
        type: 'p',
        text:
          'QA Agents and Call Agents get a **Workspace** group with just the pages their role uses, and the same **Help** group.',
      },
      {
        type: 'p',
        text:
          'The sidebar starts as a narrow rail of icons with short labels; point at an icon to see the page’s full name. `Collapse` at the bottom of the sidebar switches between the rail and the full sidebar. In the full sidebar you can hide a group by clicking its heading and drag the right edge to change the width. The sidebar remembers these choices on this browser. On a phone, the menu button in the top bar opens the sidebar, and it closes after you pick a page.',
      },
      {
        type: 'p',
        text:
          'For Admins and Super Admins, a number next to **Jobs** (a dot in the rail) counts jobs that finished processing since you last looked at the Jobs page. Opening Jobs clears it.',
      },
      {
        type: 'p',
        text:
          'Every page except the home page has a `Back` button and a breadcrumb trail above its title. The logo in the top bar takes you home.',
      },
      { type: 'h3', text: 'Search from anywhere' },
      {
        type: 'p',
        text:
          'The search box in the top bar looks up a call, agent or campaign. Press `/` to jump to it from any page, then press `Enter` to open the Search page with your words filled in. The Search page is for Admins and Super Admins.',
      },
      { type: 'h3', text: 'The help button' },
      {
        type: 'p',
        text:
          'The help button (the "i" in the top bar) opens a panel about the page you are on: what it is for and a list of what you can do on it. When the page has a tour, the panel offers `Take a tour of this page`, which walks you through the controls one at a time.',
      },
      {
        type: 'p',
        text:
          'A page’s tour also runs by itself the first time you open that page. In a tour, `Next` and `Back` move between steps and `Skip tour` ends it; `Don\'t show tours automatically` stops tours starting by themselves. At the bottom of the help panel you can `Replay the welcome tour`, or choose `Reset tours — show them again on each page`.',
      },
      { type: 'h3', text: 'Your account menu' },
      {
        type: 'p',
        text:
          'Your initials in the top-right open the account menu. It switches between `Dark mode` and `Light mode` and signs you out with `Sign Out`. For Super Admins, `Super Admin Settings` also leads to `User Management`. The theme choice is remembered on this browser; until you choose one, the console follows your computer’s setting.',
      },
      {
        type: 'note',
        variant: 'tip',
        tag: 'Tips cards',
        text:
          'Several pages carry a collapsible tips card near the top. Each card remembers whether you opened or closed it, so closing one does not close the others.',
      },
    ],
  },

  {
    id: 'concepts',
    group: 'Start here',
    title: 'The building blocks',
    lede: 'Learn these words and the rest of the console explains itself.',
    roles: ALL_ROLES,
    blocks: [
      {
        type: 'defs',
        items: [
          {
            term: 'Campaign',
            text: 'A group of calls that share a purpose, such as a product line or a team. A campaign has the scorecards that can be used for its calls, and can hold knowledge base files the AI reads when grading. Every call belongs to exactly one campaign.',
          },
          {
            term: 'Scorecard',
            text: 'The set of questions a call is graded against, with the expected criteria for each. A campaign links to scorecards, and one scorecard can be linked to several campaigns, so editing it affects all of them.',
          },
          {
            term: 'Job',
            text: 'One batch of recordings brought in together: an upload, a Genesys fetch or a scheduled run. Jobs are how you follow processing, hand calls to QA agents and export results.',
          },
          {
            term: 'Assessment',
            text: 'The graded result for one call: an answer, a reason and evidence for each question, a QA score, and a compliance result. The AI produces it; a reviewer confirms or corrects it and then marks the call assessed, which locks it.',
          },
          {
            term: 'Override',
            text: 'A reviewer’s correction to the AI’s answer on a question, made with `Override` on the call. Every override is kept in the {{Audit|#audit}} trail.',
          },
          {
            term: 'Dispute',
            text: 'An objection to an assessment, raised by the call agent (or their General Manager) after the call is assessed. Each call can be disputed once. A reviewer resolves it with `No Change Needed` or reopens the call with `Change Assessment`, and the whole exchange is kept. See {{Disputes|#disputes}}.',
          },
        ],
      },
      { type: 'h3', text: 'Two settings that change how a call counts' },
      {
        type: 'defs',
        items: [
          {
            term: 'Compliance-only',
            text: 'A scorecard can be compliance-only: every question is a pass/fail compliance check and the call gets no QA score, only a compliance result. Other scorecards use yes/no scoring, with weighted Quality questions alongside Compliance and Conduct questions.',
          },
          {
            term: 'Non-sales',
            text: 'A QA agent, Admin or Super Admin can mark an unsuccessful call as non-sales so it can be left out of scores. The call can be marked as sales again later.',
          },
        ],
      },
      { type: 'h3', text: 'Agent records and teams' },
      {
        type: 'p',
        text:
          'Besides sign-in accounts, the console keeps an **agent record** for each person whose calls are graded. A Super Admin can give an agent record a role and say who the agent reports to in Agent Management. These roles are separate from the four sign-in roles above.',
      },
      {
        type: 'defs',
        items: [
          {
            term: 'General Manager',
            text: 'A call agent who leads a team. Anyone except another General Manager can report to one. A General Manager sees and can dispute their team’s calls on My Calls, and uses `Manage my team` to add agents who report to no one, or take their own agents off the team. See {{Managing your team|#my-team}}.',
          },
          {
            term: 'Junior Manager',
            text: 'A team member who must report to a General Manager. Only a Super Admin can move a Junior Manager off a team. The role gives no extra access.',
          },
          {
            term: 'QA Assessor',
            text: 'A label on the agent record. It gives no extra access; what a person can do in the console comes from their sign-in role.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'Order of setup',
        text:
          'Scorecards and campaigns come before recordings. A recording cannot be uploaded without choosing its campaign and one of that campaign’s scorecards, and once a call is submitted it keeps the pair it went in with.',
      },
    ],
  },
];

export default gettingStarted;
